import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import { AnotherPregnancyType } from '@app/modules/service-data/types/nippt.types';
import { isRecentPregnancyRelevant } from '@app/modules/service-data/validators/recent-pregnancy.validator';
import { momentToNgbDate } from '@core/utils/core.util';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { InputValidationStatusDirective } from '@shared/directives/input-validation-status.directive';
import { controlContainerProvider } from '@shared/helpers';
import moment from 'moment';
import { startWith } from 'rxjs';

// NIPPT: "Was the patient pregnant with another child in the last 6 months?"
// Shown only when the gestation age is under 8 weeks.
// Rendered inside the `anotherPregnancy` FormGroup.
@Component({
  selector: 'app-service-data-form-recent-pregnancy',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    NgbDatepickerModule,
    SelectComponent,
    SelectOptionComponent,
    ValidationErrorComponent,
    InputValidationStatusDirective,
  ],
  templateUrl: './service-data-form-recent-pregnancy.component.html',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataFormRecentPregnancyComponent implements OnInit {
  private controlContainer = inject(ControlContainer);
  private destroyRef = inject(DestroyRef);

  readonly fieldSet = input.required<string[]>();

  protected readonly isRelevant = signal(false);
  protected readonly wasPregnant = signal(false);

  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly outcomes = Object.values(AnotherPregnancyType);
  protected readonly momentToNgbDate = momentToNgbDate;
  protected readonly minDate = moment().subtract(6, 'months');
  protected readonly maxDate = moment();

  protected get form() {
    return this.controlContainer.control as FormGroup;
  }

  ngOnInit(): void {
    // Gestation age lives in the current pregnancy section
    const gestationWeekControl = this.form.root.get(
      'serviceInformation.shared.currentPregnancyInformation.gestationAge.week'
    );
    const wasPregnantControl = this.form.get('wasPregnant');

    gestationWeekControl.valueChanges
      .pipe(
        startWith(gestationWeekControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((week) => {
        const isRelevant = isRecentPregnancyRelevant(week);
        const hasWeek = week !== null && week !== undefined && week !== '';
        this.isRelevant.set(isRelevant);
        // Only clear the answer once the gestation age is known to be 8w+
        if (hasWeek && !isRelevant && wasPregnantControl.value !== null) {
          this.form.reset({
            wasPregnant: null,
            type: null,
            date: null,
          });
        }
      });

    wasPregnantControl.valueChanges
      .pipe(
        startWith(wasPregnantControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((wasPregnant: boolean) => {
        this.wasPregnant.set(!!wasPregnant);
        if (!wasPregnant) {
          this.form.get('type').reset(null, { emitEvent: false });
          this.form.get('date').reset(null, { emitEvent: false });
        }
      });
  }
}
